import React, { PropsWithChildren } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

import { useAppSelector } from 'state/hooks';
import { selectRmCreateRouteModal } from 'state/routeMonitoringSlice';
import { selectUser } from 'state/userSlice';

import { AdminLink } from 'features/login/AdminLink';

import { WalkmeHelpButton, WalkmeWhatsNewButton } from '../login/WalkMe';
import NotificationPanel from './NotificationPanel';
import { ProductSwitcher } from './ProductSwitcher';
import { ProjectSwitcher } from './ProjectSwitcher';
import { UserDropdown } from './UserDropdown';
import { ReactComponent as SunBurstLogo } from './streetlight_logo.svg';
import { ReactComponent as StlTmLogo } from './streetlight_traffic_monitor.svg';

import styles from './AppHeader.module.css';

export function AppLogo({ height }: { height?: number }) {
  return (
    <StlTmLogo
      className={styles.logo}
      style={{ height }}
      alt="streetlight_logo"
    />
  );
}

export function AppHeader({ children }: { children?: React.ReactNode }) {
  return (
    <header className={styles.AppHeader}>
      <AppLogo />
      {children}
    </header>
  );
}

export function WholeScreenModalHeader({
  children,
  close,
}: { close: () => void } & PropsWithChildren) {
  const user = useAppSelector(selectUser);
  return (
    <header className={styles.AppHeader}>
      <SunBurstLogo alt="Streetlight sunburst" />
      {children}
      <div className={styles.spacer} />
      <div className={styles.user_info}>
        {user.has_hidden_features && <AdminLink />}
        <WalkmeWhatsNewButton />
        <WalkmeHelpButton />
        <IconButton onClick={close}>
          <CloseIcon />
        </IconButton>
      </div>
    </header>
  );
}

export function LoggedInAppHeader({
  children,
  hideWalkMeButtons = false,
}: React.PropsWithChildren & { hideWalkMeButtons?: boolean }) {
  const user = useAppSelector(selectUser);
  return (
    <AppHeader>
      {children}
      <div className={styles.spacer} />
      <div className={styles.user_info}>
        {user.has_hidden_features && <AdminLink />}
        {!hideWalkMeButtons && (
          <>
            <WalkmeWhatsNewButton />
            <WalkmeHelpButton />
          </>
        )}
        <NotificationPanel />
        <ProductSwitcher />
        <UserDropdown />
      </div>
    </AppHeader>
  );
}

// // https://blog.logrocket.com/validate-react-props-proptypes

export function ProjectAppHeader() {
  const rmModalOpen = useAppSelector(selectRmCreateRouteModal);

  return (
    <LoggedInAppHeader hideWalkMeButtons={rmModalOpen}>
      <div className={styles.app_info}>
        <span className={styles.project_name}>
          <ProjectSwitcher />
        </span>
      </div>
    </LoggedInAppHeader>
  );
}

export function NotificationsAppHeader({ close }: { close: () => void }) {
  const user = useAppSelector(selectUser);
  return (
    <AppHeader>
      <div className={styles.app_info}>
        <span className={styles.project_name}>
          <ProjectSwitcher />
        </span>
      </div>
      <div className={styles.spacer} />
      <div className={styles.user_info}>
        {user.has_hidden_features && <AdminLink />}
        <WalkmeWhatsNewButton />
        <WalkmeHelpButton />
        <IconButton onClick={close}>
          <CloseIcon />
        </IconButton>
      </div>
    </AppHeader>
  );
}
